// Accounts Receivable register (requirement 11) — a read-only assembly of
// approved_invoice_snapshots + receipt_allocations + the client's active tax
// profile's payment terms. Nothing here is stored; it's computed fresh every
// time so it can never drift from the underlying invoices/receipts.
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import { calculateOutstanding, agingBucket, receivableStatus, daysBetween, addDays } from "./receivablesCalculator";
import { getActiveProfile } from "./clientTaxProfileService";
import { creditAdjustmentFor } from "./excessAdjustment";
import { activeSnapshotDocs } from "./voidedInvoice";

const SNAPSHOTS = "approved_invoice_snapshots";
const ALLOCATIONS = "receipt_allocations";
const ADJUSTMENTS = "ar_credit_adjustments";

export async function buildAccountsReceivableRows(clientCode, { asOfDate, tolerance = 5 } = {}) {
  const today = asOfDate || new Date().toISOString().slice(0, 10);
  const [snapshotsResult, allocationsResult, taxProfile, adjustmentsResult] = await Promise.all([
    getDocs(query(collection(db, SNAPSHOTS), where("clientCode", "==", clientCode))),
    getDocs(query(collection(db, ALLOCATIONS), where("clientCode", "==", clientCode))),
    getActiveProfile(clientCode),
    getDocs(query(collection(db, ADJUSTMENTS), where("clientCode", "==", clientCode)))
  ]);
  // Excess moved onto / off an invoice by a manual adjustment (reversed ones are ignored).
  const creditAdjustments = adjustmentsResult.docs.map((d) => d.data());

  const allocationsBySnapshot = new Map();
  allocationsResult.docs.forEach((d) => {
    const data = d.data();
    if (data.reversed) return;
    const list = allocationsBySnapshot.get(data.snapshotId) || [];
    list.push(data);
    allocationsBySnapshot.set(data.snapshotId, list);
  });

  // Only invoices approved before terms were stored on the snapshot fall back to
  // the client's current profile; everything newer keeps the terms it was
  // approved under, so changing a client's terms never re-ages old invoices.
  const profileTermsDays = taxProfile?.paymentTermsDays ?? 30;

  return activeSnapshotDocs(snapshotsResult).map((d) => {
    const snapshot = { id: d.id, ...d.data() };
    const allocations = allocationsBySnapshot.get(d.id) || [];
    const allocatedAmount = allocations.reduce((sum, a) => sum + Number(a.allocatedAmount || 0), 0);
    const whtRecognized = allocations.reduce((sum, a) => sum + Number(a.whtRecognized || 0), 0);
    const gstWithheldRecognized = allocations.reduce((sum, a) => sum + Number(a.gstWithheldRecognized || 0), 0);
    const otherDeductionsRecognized = allocations.reduce((sum, a) => sum + Number(a.otherDeductionsRecognized || 0), 0);

    const creditAdjustment = creditAdjustmentFor(d.id, creditAdjustments);
    const outstanding = calculateOutstanding({
      grossAmount: snapshot.totals?.grossAmount, allocatedAmount, whtRecognized, gstWithheldRecognized, otherDeductionsRecognized,
      creditAdjustments: creditAdjustment
    });
    const paymentTermsDays = snapshot.paymentTermsDays != null ? Number(snapshot.paymentTermsDays) : profileTermsDays;
    const dueDate = addDays(snapshot.invoiceDate, paymentTermsDays);
    const received = allocatedAmount + whtRecognized + gstWithheldRecognized + otherDeductionsRecognized + Math.abs(Number(creditAdjustment) || 0);
    const daysPastDue = daysBetween(dueDate, today);
    const isPastDue = daysPastDue > 0;

    return {
      ...snapshot,
      allocatedAmount, whtRecognized, gstWithheldRecognized, otherDeductionsRecognized, creditAdjustment,
      outstanding,
      paymentTermsDays,
      dueDate,
      daysPastDue: Math.max(0, daysPastDue),
      agingBucket: agingBucket(daysPastDue),
      status: receivableStatus({ outstanding, isPastDue, tolerance, received })
    };
  });
}

export function summarizeAging(rows) {
  return rows.reduce((sum, row) => {
    if (row.outstanding <= 0) return sum;
    sum[row.agingBucket] = (sum[row.agingBucket] || 0) + row.outstanding;
    sum.total += row.outstanding;
    return sum;
  }, { CURRENT: 0, "1-30": 0, "31-60": 0, "61-90": 0, "90+": 0, total: 0 });
}
